import { blue, green, grey, lightBlue, purple } from '@mui/material/colors';
import Expand from 'assets/images/icons/expand.png';
import { useRef } from 'react';
import Chart from 'react-apexcharts';
export const LabelType = {
  String: 'category',
  Date: 'auto',
  Number: 'numeric',
};
const ONE_DAY = 86400;
const DEFAULT_GRADIENT_COLORS = [lightBlue[200], blue[400]];
const DECIMAL_POINTS = 2;
const DEFAULT_STROKE_WIDTH = 2;
const DEFAULT_COLORS = [blue[400], green[400], purple[400]];

/**
 * Creates a line chart based on react js.
 * If the datasets' data is made of objects, the value (number) itself should be under
 * a property named 'value'
 * @param {*} props.isZoomEnabled
 * @param {*} props.datasets
 * @param {*} props.isLineCurved
 * @param {*} props.isHidden
 * @param {*} props.xAxis.labelType
 * @param {*} props.xAxis.show
 * @param {*} props.fill
 * @param {*} props.strokeWidth
 * @param {*} props.colors
 * @param {*} props.detailed // wether you see stuff like lines and axis labels
 * @returns
 */
const OcpLineChart = props => {
  const chartRef = useRef(null);

  // console.log("props.datasets:", props.datasets);

  let series = props.datasets.map(dateDataset => {
    if (dateDataset.data) {
      return {
        name: dateDataset.date,
        data: Object.entries(dateDataset.data).map(([index, obj]) => ({
          // x: getHour(key),
          x: obj.dateTime,
          y: obj.ocpAvg,
        })),
      };
    } else {
      return {
        name: dateDataset.date,
        data: null,
      };
    }
  });
  const largestSeriesLength = Math.max(0, ...series.map(seriesItem => seriesItem?.data?.length ?? 0));
  const showPointValues = largestSeriesLength <= 50;

  const getFormatByLabelType = labelType => {
    if (labelType === LabelType.Date) {
      return 'dd/MM/ HH:mm';
    }

    return undefined;
  };

  const title = typeof props.title === 'string' ? props.title : 'CO2';

  const options = () => {
    return {
      datasets: props.datasets,
      chart: {
        id: props.sensorId,
        type: props.fill ? 'area' : 'line',
        stacked: false,
        zoom: {
          type: 'x',
          enabled: true,
          autoScaleYaxis: false,
        },
        events: {},
        toolbar: {
          export: {
            csv: {
              filename: title + ' ' + new Date().toLocaleDateString(),
              headerCategory: `${props.sensorId}: Date`,
              columnDelimiter: ',',
              dateFormatter: function (timestamp) {
                return new Date(timestamp)
                  .toLocaleDateString('en-En', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour12: false,
                    hour: '2-digit',
                    minute: '2-digit',
                    timeZone: 'UTC',
                  })
                  .replace(/,/, '');
              },
            },
          },
          autoSelected: 'zoom',
          show: props.toolbar ? props.toolbar.show : true,
          offsetX: props.toolbar ? props.toolbar.offsetX : 0,
          offsetY: props.toolbar ? props.toolbar.offsetY : 0,
          tools: {
            download: props.detailed ?? true,
            selection: props.detailed ?? true,
            zoom: props.detailed ?? true,
            zoomin: props.detailed ?? true,
            zoomout: props.detailed ?? true,
            pan: props.detailed ?? true,
            reset: props.detailed ?? true,
            // customIcons: props.onExpand
            //   ? [
            //       {
            //         title: 'Full Screen',
            //         icon: `<img alt="" height="12" src=${Expand} />`, // TODO: build and replace with "expand.png"
            //         click: props.onExpand,
            //       },
            //     ]
            //   : [],
          },
        },
      },
      legend: {
        inverseOrder: true, // Reverses the order of the legend items
      },
      dataLabels: {
        enabled: showPointValues,
        background: {
          enabled: false,
        },
        offsetY: -8,
        style: {
          colors: props.colors ?? DEFAULT_COLORS,
          fontSize: '12px',
          fontWeight: 500,
        },
        formatter: value => `${Math.round(value)}%`,
      },
      markers: {
        size: showPointValues ? 4 : 0,
      },
      tooltip: {
        shared: true,
        y: {
          formatter: value => (value === null ? 'No available data' : `${Math.round(value)}%`), // Custom tooltip text
        },
        x: {
          show: true,
          format: value => {
            return `${value - 1}:00 - ${value}:00`;
          },
        },
      },
      colors: props.colors ?? DEFAULT_COLORS,
      stroke: {
        dashArray: [15, 0],
        show: !props.fill,
        width: props.strokeWidth ?? DEFAULT_STROKE_WIDTH,
        // curve: props.isLineCurved ? 'smooth' : 'straight',
        curve: 'smooth',
        colors: props.colors ?? DEFAULT_COLORS,
      },
      xaxis: {
        tickAmount: 20,
        reversed: false,
        type: props.xAxis.labelType ?? LabelType.String,
        labels: {
          show: props.xAxis?.show ?? true,
          offsetY: 2,
          format: getFormatByLabelType(props.xAxis.labelType),
          rotate: -32,
          style: {
            colors: [''],
          },
        },
        axisTicks: {
          show: true,
          borderType: 'solid',
          color: '#000',
          height: 7,
        },
      },
      yaxis: {
        min: 0,
        max: Math.max(...props.datasets.flatMap(obj => obj.data.map(item => item.ocpAvg)), 100),
        opposite: false,
        labels: {
          style: {
            colors: grey[900],
          },
          formatter: value => Math.round(value),
        },
        title: {
          text: series.name,
          style: {
            color: grey[900],
          },
        },
      },
      grid: {
        yaxis: {
          lines: {
            show: props.detailed ?? true,
          },
        },
      },
    };
  };

  const getOptions = () => {
    const opt = options();
    if (props.fill && props.fill === true) {
      opt.fill = {
        type: 'gradient',
        gradient: {
          colorStops: [
            {
              offset: 0,
              color: props.colors ? props.colors[1] : DEFAULT_GRADIENT_COLORS[1],
              opacity: 1,
            },
            {
              offset: 100,
              color: props.colors ? props.colors[0] : DEFAULT_GRADIENT_COLORS[0],
              opacity: 1,
            },
          ],
        },
      };
    }

    return opt;
  };

  // console.log("OcpLineChart rerender!");

  return (
    <Chart
      id={props.sensorId}
      options={getOptions()}
      series={series}
      height={'100%'}
      // type={props.fill ? 'area' : 'line'}
      type="area"
      width={props.width ?? undefined}
      ref={chartRef}
    />
  );
};

export default OcpLineChart;
